import { Injectable, Inject } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { BYD_API_INJECTION_TOKEN } from '../configs/token/byd-api-injection-token';

/**
 * The api configuration service, is available as an injectable class, contains `injectedApiToken` informations.
 * An BydConfigService can be used to inject api token shared in the application to perform http request.
 * And it support multiple providers
 *
 * @see https://angular.io/guide/singleton-services#the-forroot-pattern
 * @example  constructor(public BydConfigService: BydConfigService<AppConfigModel>) {
 *            // typed according to a generic model `T` like `AppConfigModel`
 *              BydConfigService.injectedApiToken.baseUrl
 *            }
 * @example providers: [ // in the root module
 *          BydConfigService
 *          {
 *            provide: BYD_API_INJECTION_TOKEN,
 *            useValue: {
 *              apiUrl: environment.settings.ROAZHON_URL,
 *              apiScope: 'frontged',
 *            },
 *          }]
 * @example providers: [ // in the root module
 *          BydConfigService
 *            provide: BYD_API_INJECTION_TOKEN,
 *            useValue: {
 *              api_1_Url: environment.settings.API_1_URL,
 *              api_1_Scope: 'frontged',
 *             },
 *            multi: true,
 *            },
 *            {
 *            provide: BYD_API_INJECTION_TOKEN,
 *            useValue: {
 *              api_2_Url: environment.settings.API_2_URL,
 *              api_2_Scope: 'frontged',
 *             },
 *            multi: true,
 *          }]
 */
// @dynamic
@Injectable({
  providedIn: 'root',
})
export class BydConfigService<T> {
  /**
   * The HTTP headers.
   */
  private _headers = new HttpHeaders().set('Content-Type', 'application/json');

  /**
   * Return the current headers.
   *
   * @returns HttpHeaders
   */
  public get headers(): HttpHeaders {
    return this._headers;
  }

  /**
   * Change the current _headers value.
   *
   * @param value HttpHeaders
   */
  public set headers(value: HttpHeaders) {
    this._headers = value;
  }

  /**
   * Creates an instance of BydConfigService.
   *
   * @param injectedApiToken the token used in the dependency injection of BydConfigService provider.
   */
  constructor(@Inject(BYD_API_INJECTION_TOKEN) public injectedApiToken: T) {
    if (Array.isArray(injectedApiToken)) this.injectedApiToken = this.formatInjectedApiToken<T>(injectedApiToken);
  }

  /**
   * Spread the injectedApiToken array to a simplified object.
   *
   * @example [{
   *            API_1_URL: 'https://api.example1.com',
   *            API_1_SCOPE: 'frontged'
   *          }
   *          {
   *            API_2_URL: 'https://api.example2.com',
   *            API_2_SCOPE: 'frontged'
   *          }]
   * To
   *          {
   *            API_1_URL: 'https://api.example1.com',
   *            API_1_SCOPE: 'frontged'
   *            API_2_URL: 'https://api.example2.com',
   *            API_2_SCOPE: 'admin'
   *          }
   * @param injectedApiToken the injected configuration
   * @returns an object U
   */
  formatInjectedApiToken<U>(injectedApiToken: U[]): U {
    let injectedToken: U;
    injectedApiToken.forEach((config) => {
      injectedToken = { ...injectedToken, ...config };
    });
    return injectedToken;
  }
}
